import { registerLocaleData } from '@angular/common';
import localeEnCA from '@angular/common/locales/en-CA';
import localeSpan from '@angular/common/locales/es-MX';
import localeFrCaExtra from '@angular/common/locales/extra/fr-CA';
import localeFr from '@angular/common/locales/fr';
import localeFrCA from '@angular/common/locales/fr-CA';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  DOCUMENT,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { JwtHelperService } from '@auth0/angular-jwt';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { UserIdleService } from 'angular-user-idle';
import * as fromRoot from 'app/core/store';
import { layoutAnimations } from 'app/shared/animations/animations';
import { AuthService } from 'app/shared/authentication/services/auth.service';
import { Clinic } from 'app/shared/clinics/models/clinic.model';
import { getDomainName, setDefaultOptions } from 'app/shared/dialogs/utils/app.utils';
import { DynamicLoaderService } from 'app/shared/dynamic/services/dynamic-loader.service';
import { LayoutSize } from 'app/shared/layout/models/layout-size.enum';
import { SideBarStateEnum } from 'app/shared/layout/models/side-bar-state.enum';
import { ColorFormatHelperService } from 'app/shared/layout/services/color-format-helper.service';
import { HttpLoaderIndicatorService } from 'app/shared/layout/services/http-loader-indicator.service';
import { WINDOW } from 'app/shared/layout/services/window-provider.service';
import { WindowSizeModel } from 'app/shared/layout/services/window-size.model';
import { PortalSetupHttpService } from 'app/shared/portal/services/portal-setup-http.service';
import { WebPartnerAuditHttpService } from 'app/shared/portal/services/web-partner-audit-http.service';
import { Identifier, LocalStorageService } from 'app/shared/storage/services/local-storage.service';
import { loadMessages, locale } from 'devextreme/localization';
import moment from 'moment';
import { Observable, Subscription } from 'rxjs';
import { filter, first, map, mergeMap, tap } from 'rxjs/operators';

import { devextremeLocalisation } from '../assets/devextreme-localisation';
import { setNavigationState, setUserIdle, setWindowSizeChanged } from './core/store';
import { ClinicGroupFranchiseBrandingLogo } from './shared/clinics/models/clinic-group-franchise-branding-logo.model';
import { LogoDimension } from './shared/clinics/models/logo-dimension.enum';
import { BookingAppSessionCreation } from './shared/portal/models/booking-app-session-creation.model';
import { ClinicGroupFranchise } from './shared/clinics/models/clinic-group-franchise.model';
import config from 'devextreme/core/config';
import { licenseKey } from './devextreme-key';
import { RoutingDictionaryForMobileNav } from './shared/authentication/models/routing-for-mobile.enum';
import { ReferrerPortalTermsAndConditionsSetup } from './shared/clinics/models/terms-and-conditions-setup.model';
import { newModalConfiguration } from './shared/dynamic/models/modal-configuration.model';
import { TermsAndConditionsModalComponent } from './shared/terms-and-conditions/components/terms-and-conditions-modal/terms-and-conditions-modal.component';
config({ licenseKey });

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: layoutAnimations,
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  isLoading = this.httpLoaderService.loading$;

  navigationState$?: Observable<string>;
  userSecondsIdle?: number;
  idleTimeOutLimit = 300;
  displayIdleConfirmDialog = false;
  moment = moment;
  franchise?: ClinicGroupFranchise;

  currentClinic?: Clinic;

  showRefreshPopup = false;

  trackingKey?: string;
  setupLoaded = false;
  dynamicManifest: any;
  private subscription: Subscription = new Subscription();
  private idleSubscription: Subscription = new Subscription();
  private jwtHelper = new JwtHelperService();

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(WINDOW) public window: Window,
    private store: Store<fromRoot.State>,
    private portalSetupService: PortalSetupHttpService,
    private router: Router,
    private route: ActivatedRoute,
    private translateService: TranslateService,
    private localStorageService: LocalStorageService,
    private httpLoaderService: HttpLoaderIndicatorService,
    private userIdle: UserIdleService,
    private authService: AuthService,
    private swUpdate: SwUpdate,
    private ref: ChangeDetectorRef,
    private dynamicLoaderService: DynamicLoaderService,
    private colorFormatHelperService: ColorFormatHelperService,
    private webPartnerAuditHttpService: WebPartnerAuditHttpService
  ) {
    setDefaultOptions();
    AppComponent.registerLocale();
    translateService.addLangs(['en-CA', 'fr-CA', 'es-ES', 'de-DE']);
    this.subscribeRouter();
  }

  private static registerLocale(): void {
    registerLocaleData(localeFr);
    registerLocaleData(localeFrCA, localeFrCaExtra);
    registerLocaleData(localeEnCA);
    registerLocaleData(localeSpan);
  }

  @HostListener('window:resize')
  onResize(): void {
    const windowSize: WindowSizeModel = {
      width: document.documentElement.offsetWidth,
      height: document.documentElement.offsetHeight,
    };
    this.store.dispatch(setWindowSizeChanged({ windowSize: windowSize }));
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if (this.displayIdleConfirmDialog) {
      if (event.key === 'Escape') {
        this.restartIdleTimer();
      }
    }
  }

  ngOnInit(): void {
    this.subscribeToNavigationStart();
    this.localStorageService.setStorageItem(
      Identifier.TYPE_API_KEY,
      '1C34CDDF-8875-4920-809D-774B1A6412F8'
    );
    this.subscribeToQueryParamChange();
    this.subscribeToLanguageLocalStorage();
    this.subscribeToSoftwareUpdate();
    this.subcribeToLocalStorageClinic();
  }

  ngOnDestroy(): void {
    localStorage.removeItem('apiKey');
    this.subscription.unsubscribe();
    this.idleSubscription.unsubscribe();
  }

  getFullClinicInformation(clinicId: number | undefined): void {
    if (!clinicId) {
      return;
    }
    this.subscription.add(
      this.portalSetupService.getClinic(clinicId).subscribe(clinic => {
        this.localStorageService.setStorageItem(Identifier.TYPE_CLINIC, JSON.stringify(clinic));
        this.currentClinic = clinic;
        this.getModulesByClinic();
        this.setPartnerToken();
        this.setSourceToken();
        this.getClinicOnlineSetup();
      })
    );
  }

  private getModulesByFranchises(): void {
    this.subscription.add(
      this.portalSetupService.getModulesByFranchises().subscribe(modules => {
        if (modules) {
          this.localStorageService.setStorageItem(
            Identifier.TYPE_FRANCHISE_MODULES,
            JSON.stringify(modules.map(moduleId => moduleId))
          );
        }
      })
    );
  }

  getModulesByClinic(): void {
    this.subscription.add(
      this.portalSetupService.getModulesByClinic().subscribe(modules => {
        if (modules) {
          this.localStorageService.setStorageItem(
            Identifier.TYPE_CLINIC_MODULES,
            JSON.stringify(modules.map(moduleId => moduleId))
          );
        }
      })
    );
  }

  getClinicOnlineSetup(): void {
    // this.setupLoaded = true;
    // this.subscription.add(
    //   this.localStorageService.getStorageItem(Identifier.TYPE_TOKEN).subscribe(token => {
    //     if (token) {
    //       this.setIdleCheck(1440, token);

    //       this.setNavigationState(token);
    //       this.subscribeToPortalLocalStorage(token);
    //     } else {
    //       this.flushIdleTimer();
    //     }
    //   })
    // );
    this.subscription.add(
      this.portalSetupService.getClinicOnlineReferrerSetup().subscribe(onlineSetup => {
        if (onlineSetup) {
          this.localStorageService.setStorageItem(
            Identifier.TYPE_PORTAL_SETUP,
            JSON.stringify(onlineSetup)
          );
          this.setPortalColors(onlineSetup.primaryColor, onlineSetup.secondaryColor);
          this.setPortalBackground(onlineSetup.backgroundImageFileUri);
          this.subscription.add(
            this.localStorageService.getStorageItem(Identifier.TYPE_TOKEN).subscribe(token => {
              if (token) {
                this.setIdleCheck(1440, token);

                this.setNavigationState(token);
                this.subscribeToPortalLocalStorage(token);
              } else {
                this.flushIdleTimer();
              }
            })
          );
        } else {
          this.localStorageService.removeStorageItem(Identifier.TYPE_PORTAL_SETUP);
          this.subscription.add(
            this.localStorageService.getStorageItem(Identifier.TYPE_TOKEN).subscribe(token => {
              if (token) {
                this.setIdleCheck(1440, token);

                this.setNavigationState(undefined);
              } else {
                this.flushIdleTimer();
              }
            })
          );
        }
        this.setupLoaded = true;
      })
    );
  }

  appendAppleMaskIcon(url: string): void {
    try {
      const link = this.document.createElement('link');
      link.rel = 'mask-icon';
      link.href = url;
      link.setAttribute('color', '#000000');
      this.document.head.appendChild(link);

      const link2 = this.document.createElement('link');
      link2.rel = 'apple-touch-icon';
      link2.href = url;
      this.document.head.appendChild(link2);
    } catch (ex) {
      console.error('Error appending apple mask icon');
      console.error(ex);
    }
  }

  restartIdleTimer(): void {
    this.userIdle.stopWatching();
    this.displayIdleConfirmDialog = false;
    this.userIdle.startWatching();
    this.store.dispatch(setUserIdle({ isIdle: false }));
  }

  endIdleTimerAndSession(): void {
    this.flushIdleTimer();
    this.store.dispatch(setUserIdle({ isIdle: false }));
    this.dynamicLoaderService.removeAllDynamicComponents();
    this.authService.logout();
    this.navigateToLanding();
  }

  private setPortalColors(
    primaryColor: number | undefined,
    secondaryColor: number | undefined
  ): void {
    if (primaryColor && secondaryColor) {
      document.documentElement.style.setProperty(
        '--primaryColor',
        this.colorFormatHelperService.rgbLongToHex(primaryColor)
      );
      document.documentElement.style.setProperty(
        '--accentColor',
        this.colorFormatHelperService.rgbLongToHex(secondaryColor)
      );
    } else {
      document.documentElement.style.setProperty('--primaryColor', '#2d1939'); //mulberry
      document.documentElement.style.setProperty('--accentColor', '#5e3869'); //grape
    }
  }

  private setPortalBackground(backgroundImage: string | undefined): void {
    if (backgroundImage) {
      document.documentElement.style.setProperty('--backgroundImage', `url("${backgroundImage}")`);
    } else {
      document.documentElement.style.setProperty(
        '--backgroundImage',
        'url("../../../assets/images/bg.jpg")'
      );
    }
  }

  private subscribeToNavigationStart(): void {
    this.subscription.add(
      this.router.events
        .pipe(filter((event: any) => event instanceof NavigationStart))
        .subscribe(event => {
          if (event.navigationTrigger === 'popstate') {
            this.dynamicLoaderService.removeAllDynamicComponents();
          }
        })
    );
  }

  private setNavigationState(token: string | undefined): void {
    if (document.documentElement.offsetWidth < LayoutSize.lg) {
      return;
    }

    if (token && !this.jwtHelper.isTokenExpired(token)) {
      this.store.dispatch(setNavigationState({ navState: SideBarStateEnum.Open }));
    } else {
      this.store.dispatch(setNavigationState({ navState: SideBarStateEnum.Close }));
    }
  }

  private setLanguage(lang: string | null): void {
    let curLang = 'en-CA';
    if (lang && lang !== '') {
      curLang = lang;
    }
    this.translateService.setDefaultLang(curLang);
    this.translateService.use(curLang);
    locale(curLang);
    switch (curLang) {
      case 'fr-CA':
        moment.locale('fr');
        break;
      case 'de-DE':
        moment.locale('de');
        break;
      case 'es-ES':
        moment.locale('es');
        break;
      default:
        moment.locale('en');
        break;
    }
    this.ref.markForCheck();
  }

  private subscribeToSoftwareUpdate(): void {
    if (this.swUpdate.isEnabled) {
      this.subscription.add(
        this.swUpdate.versionUpdates
          .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
          .subscribe(() => {
            this.showRefreshPopup = true;

            this.ref.markForCheck();
          })
      );
    }
  }

  private subscribeRouter(): void {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd && this.trackingKey) {
        (window as any).gtag('config', this.trackingKey, {
          page_path: event.urlAfterRedirects,
        });
      }
    });
  }

  private subscribeToQueryParamChange(): void {
    this.subscription.add(
      this.router.events
        .pipe(
          first((e): e is NavigationEnd => e instanceof NavigationEnd),
          tap(() => {
            this.setPortalLanguage();
          }),
          mergeMap(() => {
            this.getModulesByFranchises();
            const subDomain = getDomainName();
            return this.portalSetupService.getFranchise(subDomain);
          }),
          map(franchise => {
            if (!franchise) {
              return undefined;
            }
            this.franchise = franchise;
            this.localStorageService.setStorageItem(
              Identifier.TYPE_FRANCHISE_ID,
              this.franchise.clinicGroupFranchiseId.toString()
            );
            this.localStorageService.setStorageItem(
              Identifier.TYPE_FRANCHISE_NAME,
              this.franchise.name.toString()
            );
            if (this.franchise.otherSupportedLanguages) {
              this.localStorageService.setStorageItem(
                Identifier.TYPE_FRANCHISE_LANGUAGE_IDS,
                JSON.stringify(this.franchise.otherSupportedLanguages)
              );
            }
            if (this.franchise.referrerPortalTermAndConditionSetup) {
              this.localStorageService.setStorageItem(
                Identifier.TYPE_TERMS_CONDITIONS,
                JSON.stringify(this.franchise.referrerPortalTermAndConditionSetup)
              );
            }
            if (this.franchise?.clinicGroupFranchiseBrandingLogos) {
              // get smallest size logo for favicon
              const logoObject72x72 = this.franchise?.clinicGroupFranchiseBrandingLogos.find(
                logo => logo.sysLogoDimensionId === LogoDimension.x72x72
              );
              if (logoObject72x72?.logoFileUri) {
                this.document
                  .querySelector('#linkFavicon')
                  ?.setAttribute('href', logoObject72x72?.logoFileUri);
              }
            }
            this._setManifestAfterFranchiseCall(this.franchise?.clinicGroupFranchiseBrandingLogos);

            if (!this.franchise.clinicGroups || this.franchise.clinicGroups.length <= 0) {
              return undefined;
            }

            const clinics: Clinic[] = [];
            this.franchise.clinicGroups.forEach(group => {
              group.clinics.forEach(clinic => {
                clinics.push(clinic);
              });
            });
            this.localStorageService.setStorageItem(
              Identifier.TYPE_FRANCHISE_CLINICS,
              JSON.stringify(clinics)
            );
            const suppliedClinicId = +this.route.snapshot.queryParams['clinicId'];
            const currentClinic = clinics.find(clinic => clinic.clinicId === suppliedClinicId);
            if (suppliedClinicId && currentClinic) {
              return currentClinic;
            } else if (clinics) {
              return this.handleClinic(clinics);
            }
            return undefined;
          })
        )
        .subscribe((handledClinic: Clinic | undefined) => {
          if (handledClinic) {
            this.localStorageService.setStorageItem(
              Identifier.TYPE_CLINIC_GROUP_ID,
              handledClinic.clinicGroupId.toString()
            );
            this.getFullClinicInformation(handledClinic.clinicId);
            const nonce = this.route.snapshot.queryParams['nonce'];
            const permanentToken = this.route.snapshot.queryParams['authorize'];
            const queryParamObj = this.route.snapshot.queryParams;
            if (nonce) {
              this.authService.loginWithNonce(nonce).subscribe(res => {
                if (res) {
                  const user = this.authService.processToken(res.token);
                  if (user) {
                    if (queryParamObj && queryParamObj['appnavigation']) {
                      this._mobileAppNavigation(+queryParamObj['appnavigation']);
                    } else {
                      this.router.navigate([], {
                        queryParams: {
                          ['nonce']: null,
                        },
                        queryParamsHandling: 'merge',
                      });
                    }
                  }
                }
              });
            }
            if (permanentToken) {
              this.subscription.add(
                this.authService.loginUserWithToken(permanentToken).subscribe({
                  next: result => {
                    if (result) {
                      const user = this.authService.processToken(result.token);
                      if (user) {
                        this.router.navigate([], {
                          queryParams: {
                            ['authorize']: null,
                          },
                          queryParamsHandling: 'merge',
                        });
                      }
                    }
                  },
                })
              );
            }
          }
        })
    );
  }

  private _setManifestAfterFranchiseCall(brandingLogoArray: ClinicGroupFranchiseBrandingLogo[]) {
    const subDomain = `https://${getDomainName()}`;

    //populate Manifest
    if (!brandingLogoArray) {
      this.dynamicManifest = this._getManifestWithDefaultAssetIcons(subDomain);
    } else {
      this.dynamicManifest = this._getManifestWithLogoFromFranchise(subDomain, brandingLogoArray);
    }

    const stringManifest = JSON.stringify(this.dynamicManifest);
    const blob = new Blob([stringManifest], { type: 'application/json' });
    const manifestURL = URL.createObjectURL(blob);
    this.document.querySelector('link[rel=manifest]')?.setAttribute('href', manifestURL);
  }

  private _getManifestWithDefaultAssetIcons(domain: any): any {
    this.appendAppleMaskIcon(`${domain}/assets/icons/icon-512x512.png`);
    return {
      name: 'Client Portal',
      short_name: 'CP',
      display: 'standalone',
      theme_color: '#317EFB',
      background_color: 'blue',
      start_url: domain,
      scope: domain,
      icons: [
        {
          src: `${domain}/assets/icons/icon-72x72.png`,
          sizes: '72x72',
          type: 'image/png',
          purpose: 'maskable any',
        },
        {
          src: `${domain}/assets/icons/icon-96x96.png`,
          sizes: '96x96',
          type: 'image/png',
          purpose: 'maskable any',
        },
        {
          src: `${domain}/assets/icons/icon-128x128.png`,
          sizes: '128x128',
          type: 'image/png',
          purpose: 'maskable any',
        },
        {
          src: `${domain}/assets/icons/icon-144x144.png`,
          sizes: '144x144',
          type: 'image/png',
          purpose: 'maskable any',
        },
        {
          src: `${domain}/assets/icons/icon-152x152.png`,
          sizes: '152x152',
          type: 'image/png',
          purpose: 'maskable any',
        },
        {
          src: `${domain}/assets/icons/icon-192x192.png`,
          sizes: '192x192',
          type: 'image/png',
          purpose: 'maskable any',
        },
        {
          src: `${domain}/assets/icons/icon-384x384.png`,
          sizes: '384x384',
          type: 'image/png',
          purpose: 'maskable any',
        },
        {
          src: `${domain}/assets/icons/icon-512x512.png`,
          sizes: '512x512',
          type: 'image/png',
          purpose: 'maskable any',
        },
      ],
    };
  }

  private _getManifestWithLogoFromFranchise(
    domain: any,
    brandingLogoArray: ClinicGroupFranchiseBrandingLogo[]
  ): any {
    const iconsArr = brandingLogoArray.map(iterator => {
      if (iterator?.sysLogoDimensionId === 8) {
        this.appendAppleMaskIcon(iterator?.logoFileUri);
      }
      return {
        src: iterator?.logoFileUri,
        sizes: this.getSize(iterator?.sysLogoDimensionId),
        type: 'image/png',
        purpose: 'maskable any',
      };
    });

    return {
      name: 'Client Portal',
      short_name: 'CP',
      display: 'standalone',
      theme_color: '#317EFB',
      background_color: 'blue',
      start_url: domain,
      scope: domain,
      icons: iconsArr,
    };
  }

  private getSize(id: number): string {
    if (!id) {
      return '';
    }

    let size = '';
    switch (id) {
      case 1:
        size = '72x72';
        break;
      case 2:
        size = '96x96';
        break;
      case 3:
        size = '128x128';
        break;
      case 4:
        size = '144x144';
        break;
      case 5:
        size = '152x152';
        break;
      case 6:
        size = '192x192';
        break;
      case 7:
        size = '384x384';
        break;
      case 8:
        size = '512x512';
        break;
      case 9:
        size = '180x180';
        break;
      default:
        break;
    }
    return size;
  }

  private subcribeToLocalStorageClinic(): void {
    this.subscription.add(
      this.localStorageService.getStorageModel<Clinic>(Identifier.TYPE_CLINIC).subscribe(clinic => {
        if (
          clinic &&
          (!this.currentClinic || this.currentClinic.clinicId !== clinic.clinicId) &&
          this.setupLoaded
        ) {
          this.localStorageService.setStorageItem(
            Identifier.TYPE_CLINIC_GROUP_ID,
            clinic.clinicGroupId.toString()
          );
          this.getFullClinicInformation(clinic.clinicId);
        }
      })
    );
  }

  private setPortalLanguage(): void {
    let currentLang = localStorage.getItem('culture') || 'en-CA';
    const queryLang = this.route.snapshot.queryParams['lang'];
    if (queryLang && this.translateService.store.langs.indexOf(queryLang) > -1) {
      currentLang = queryLang;
    }
    this.localStorageService.setStorageItem(Identifier.TYPE_LANGUAGE, currentLang);
    loadMessages(devextremeLocalisation);
  }

  private setPartnerToken(): void {
    const queryToken = this.route.snapshot.queryParams['partner'];
    if (queryToken) {
      this.store.dispatch(fromRoot.setPartnerToken({ partnerToken: queryToken }));
      this.subscription.add(
        this.webPartnerAuditHttpService.insertWebPartnerReferrerAuditOfVisit(queryToken).subscribe()
      );
      this.subscription.add(
        this.webPartnerAuditHttpService
          .getWebPartnerReferrerCanBookAnonymously(queryToken)
          .subscribe(bool => {
            this.store.dispatch(fromRoot.setPartnerUnauthBooking({ partnerUnauthBooking: bool }));
          })
      );

      this.router.navigate([], {
        queryParams: { ['partner']: null },
        queryParamsHandling: 'merge',
      });
    }
  }

  private setSourceToken(): void {
    const practitionerId = this.returnPotentialPractitionerId(this.route.root.firstChild, '');

    const sourceToken: BookingAppSessionCreation = {
      urlToken: this.route.snapshot.queryParams['source']
        ? this.route.snapshot.queryParams['source']
        : this.route.snapshot.queryParams['baid'],
      referredPractitionerId: practitionerId ? +practitionerId : undefined,
    };

    if (sourceToken.urlToken) {
      this.subscription.add(
        this.portalSetupService.createBookingAppSession(sourceToken).subscribe(appSession => {
          if (appSession) {
            this.localStorageService.setStorageItem(
              Identifier.TYPE_SOURCE_SESSION_TOKEN,
              JSON.stringify(appSession)
            );
          }
          this.router.navigate([], {
            queryParams: { ['source']: null, ['baid']: null },
            queryParamsHandling: 'merge',
          });
        })
      );
    }
  }

  private returnPotentialPractitionerId(child: ActivatedRoute | null, currentId: string): string {
    child?.paramMap.forEach(pm => {
      const potentialId = pm.get('practitionerId');
      currentId = potentialId ? potentialId : currentId;
    });

    if (child?.firstChild) {
      return this.returnPotentialPractitionerId(child?.firstChild, currentId);
    } else {
      return currentId;
    }
  }

  private subscribeToPortalLocalStorage(token: string | undefined): void {
    if (token && !this.jwtHelper.isTokenExpired(token)) {
      this.portalSetupService.getReferrerPortalStatus().subscribe(termsId => {
        if (termsId) {
          this.store.dispatch(fromRoot.setTermsId({ termsId: termsId }));
          const terms = localStorage.getItem(Identifier.TYPE_TERMS_CONDITIONS);
          if (!terms) {
            return;
          }
          const currentTerms = JSON.parse(terms) as ReferrerPortalTermsAndConditionsSetup;
          if (termsId !== currentTerms.id && currentTerms.showOnClientFirstLogin) {
            this.authService
              .getTermsAndConditions(currentTerms.termAndConditionFileUri)
              .subscribe(blob => {
                const reader = new FileReader();
                reader.readAsDataURL(blob);
                reader.onload = (event: any) => {
                  this.openFormModal(event.target.result.split(',')[1], currentTerms);
                };
                reader.onerror = () => {
                  return;
                };
              });
          }
        }
      });
    }
  }

  private openFormModal(
    content: string,
    currentTerms: ReferrerPortalTermsAndConditionsSetup
  ): void {
    const config = newModalConfiguration({
      panelClass: 'popup small',
      animation: 'scale',
      outsideClick: false,
    });
    const data = {
      base64: content,
    };
    this.dynamicLoaderService
      .open<TermsAndConditionsModalComponent>(TermsAndConditionsModalComponent, config, data)
      .then(container => {
        const termsModalComponent = (
          container.componentRef as ComponentRef<TermsAndConditionsModalComponent>
        ).instance;

        this.subscription.add(
          termsModalComponent.acceptClick.subscribe(() => {
            console.log(currentTerms);

            // TO DO Put in Logic
            this.authService.createTermsAndConditionsAccept(currentTerms.id).subscribe(
              () => {
                container.close();
              },
              () => {
                container.close();
              }
            );
          })
        );

        this.subscription.add(
          termsModalComponent.signOutClick.subscribe(() => {
            container.close();
            this.authService.logout();
            this.navigateToLanding();
          })
        );
      });
  }

  private subscribeToLanguageLocalStorage(): void {
    this.subscription.add(
      this.localStorageService
        .getStorageItem(Identifier.TYPE_LANGUAGE)
        .subscribe(lang => this.setLanguage(lang))
    );
  }

  private setIdleCheck(inactivityInMinutes: number, token: string): void {
    this.flushIdleTimer();
    this.userIdle.setConfigValues({
      idle: inactivityInMinutes * 60,
      timeout: this.idleTimeOutLimit,
      ping: 120,
    });
    if (token && !this.jwtHelper.isTokenExpired(token)) {
      this.startWatching();
      this.idleSubscription.add(
        this.store
          .pipe(
            select(fromRoot.getUserIdle),
            filter((idle: boolean | undefined) => !!idle)
          )
          .subscribe(() => this.showConfirmDialog())
      );
      this.subscribeToTimerStart();
      this.subscribeToTimerTimeOut();
    } else {
      this.store.dispatch(setUserIdle({ isIdle: false }));
    }
  }

  private showConfirmDialog(): void {
    this.displayIdleConfirmDialog = true;
    this.ref.markForCheck();
  }

  private subscribeToTimerStart(): void {
    this.idleSubscription.add(
      this.userIdle.onTimerStart().subscribe(count => {
        if (count === 1) {
          this.store.dispatch(setUserIdle({ isIdle: true }));
        }
        this.userSecondsIdle = count;
        console.log(this.userSecondsIdle);
        this.ref.markForCheck();
      })
    );
  }

  private subscribeToTimerTimeOut(): void {
    this.idleSubscription.add(
      this.userIdle.onTimeout().subscribe(() => {
        if (this.displayIdleConfirmDialog) {
          this.displayIdleConfirmDialog = false;
        }
        this.endIdleTimerAndSession();
      })
    );
  }

  private startWatching(): void {
    this.userIdle.startWatching();
  }

  private flushIdleTimer(): void {
    this.userIdle.stopWatching();
    this.displayIdleConfirmDialog = false;
    this.idleSubscription.unsubscribe();
    this.idleSubscription = new Subscription();
  }

  private handleClinic(clinics: Clinic[]): Clinic | undefined {
    if (clinics.length >= 1) {
      const currentClinicJson = localStorage.getItem('clinic');
      if (!currentClinicJson) {
        return clinics[0];
      }
      const currentClinic = JSON.parse(currentClinicJson);
      if (!currentClinic || !clinics.find(cl => cl.clinicId === currentClinic.clinicId)) {
        return clinics[0];
      } else {
        return currentClinic;
      }
    }
    return undefined;
  }

  private navigateToLanding(): void {
    this.router.navigate(['landing']).then();
  }

  updateToLatest(): void {
    this.swUpdate.activateUpdate().then(() => document.location.reload());
  }

  private _mobileAppNavigation(param: number) {
    let routeString = '';
    switch (param) {
      case RoutingDictionaryForMobileNav.Dashboard.enum:
        routeString = RoutingDictionaryForMobileNav.Dashboard.route;
        break;
      case RoutingDictionaryForMobileNav.Landing.enum:
        routeString = RoutingDictionaryForMobileNav.Landing.route;
        break;
      case RoutingDictionaryForMobileNav.BookOnline.enum:
        routeString = RoutingDictionaryForMobileNav.BookOnline.route;
        break;
      case RoutingDictionaryForMobileNav.Appointments.enum:
        routeString = RoutingDictionaryForMobileNav.Appointments.route;
        break;
      case RoutingDictionaryForMobileNav.Documents.enum:
        routeString = RoutingDictionaryForMobileNav.Documents.route;
        break;
      case RoutingDictionaryForMobileNav.Education.enum:
        routeString = RoutingDictionaryForMobileNav.Education.route;
        break;
      case RoutingDictionaryForMobileNav.Reports.enum:
        routeString = RoutingDictionaryForMobileNav.Reports.route;
        break;
      case RoutingDictionaryForMobileNav.PlanOfCare.enum:
        routeString = RoutingDictionaryForMobileNav.PlanOfCare.route;
        break;
      case RoutingDictionaryForMobileNav.VitalSigns.enum:
        routeString = RoutingDictionaryForMobileNav.VitalSigns.route;
        break;
      case RoutingDictionaryForMobileNav.OutcomeMeasures.enum:
        routeString = RoutingDictionaryForMobileNav.OutcomeMeasures.route;
        break;
      case RoutingDictionaryForMobileNav.ExercisePrograms.enum:
        routeString = RoutingDictionaryForMobileNav.ExercisePrograms.route;
        break;
      case RoutingDictionaryForMobileNav.Billing.enum:
        routeString = RoutingDictionaryForMobileNav.Billing.route;
        break;
      case RoutingDictionaryForMobileNav.SecureMessaging.enum:
        routeString = RoutingDictionaryForMobileNav.SecureMessaging.route;
        break;
      case RoutingDictionaryForMobileNav.GiftCertificates.enum:
        routeString = RoutingDictionaryForMobileNav.GiftCertificates.route;
        break;
      case RoutingDictionaryForMobileNav.Feedback.enum:
        routeString = RoutingDictionaryForMobileNav.Feedback.route;
        break;
      case RoutingDictionaryForMobileNav.AppointmentsConfirmPresence.enum:
        routeString = RoutingDictionaryForMobileNav.AppointmentsConfirmPresence.route;
        break;
      case RoutingDictionaryForMobileNav.DocumentsToSign.enum:
        routeString = RoutingDictionaryForMobileNav.DocumentsToSign.route;
        break;
      case RoutingDictionaryForMobileNav.DocumentsToFill.enum:
        routeString = RoutingDictionaryForMobileNav.DocumentsToFill.route;
        break;
      case RoutingDictionaryForMobileNav.BookOnlineFollowUps.enum:
        routeString = RoutingDictionaryForMobileNav.BookOnlineFollowUps.route;
        break;

      default:
        routeString = '';
    }

    this.router.navigate([routeString], {
      queryParams: {
        ['nonce']: null,
        ['appnavigation']: null,
      },
      queryParamsHandling: 'merge',
    });
  }
}
